/**
 * GhostTalk Crypto Utilities
 * End-to-end encryption using Web Crypto API
 * All keys are ephemeral and never stored
 */

// Generate ECDH key pair for key exchange
export async function generateKeyPair() {
  const keyPair = await window.crypto.subtle.generateKey(
    {
      name: "ECDH",
      namedCurve: "P-256",
    },
    true, // extractable
    ["deriveKey", "deriveBits"]
  );
  return keyPair;
}

// Export public key to share with peer
export async function exportPublicKey(publicKey) {
  const exported = await window.crypto.subtle.exportKey("raw", publicKey);
  return arrayBufferToBase64(exported);
}

// Import peer's public key
export async function importPublicKey(base64Key) {
  const keyData = base64ToArrayBuffer(base64Key);
  return await window.crypto.subtle.importKey(
    "raw",
    keyData,
    {
      name: "ECDH",
      namedCurve: "P-256",
    },
    true,
    []
  );
}

// Derive shared secret from own private key and peer's public key
export async function deriveSharedKey(privateKey, peerPublicKey) {
  const sharedKey = await window.crypto.subtle.deriveKey(
    {
      name: "ECDH",
      public: peerPublicKey,
    },
    privateKey,
    {
      name: "AES-GCM",
      length: 256,
    },
    false, // not extractable
    ["encrypt", "decrypt"]
  );
  return sharedKey;
}

// Encrypt message using shared key
export async function encryptMessage(sharedKey, plaintext) {
  const encoder = new TextEncoder();
  const data = encoder.encode(plaintext);
  
  // Generate random IV for each message
  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  
  const ciphertext = await window.crypto.subtle.encrypt(
    {
      name: "AES-GCM",
      iv: iv,
    },
    sharedKey,
    data
  );
  
  // Combine IV and ciphertext
  return {
    iv: arrayBufferToBase64(iv),
    ciphertext: arrayBufferToBase64(ciphertext),
  };
}

// Decrypt message using shared key
export async function decryptMessage(sharedKey, encryptedData) {
  const iv = base64ToArrayBuffer(encryptedData.iv);
  const ciphertext = base64ToArrayBuffer(encryptedData.ciphertext);
  
  const decrypted = await window.crypto.subtle.decrypt(
    {
      name: "AES-GCM",
      iv: iv,
    },
    sharedKey,
    ciphertext
  );
  
  const decoder = new TextDecoder();
  return decoder.decode(decrypted);
}

// Helper: ArrayBuffer to Base64
function arrayBufferToBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
}

// Helper: Base64 to ArrayBuffer
function base64ToArrayBuffer(base64) {
  const binary = window.atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

// Generate a fingerprint for key verification (optional display)
export async function getKeyFingerprint(publicKey) {
  const exported = await window.crypto.subtle.exportKey("raw", publicKey);
  const hash = await window.crypto.subtle.digest("SHA-256", exported);
  const hashArray = Array.from(new Uint8Array(hash));
  // Return first 8 bytes as hex for display
  return hashArray.slice(0, 8).map(b => b.toString(16).padStart(2, "0")).join("");
}
